/* eslint-disable i18next/no-literal-string */
import {FormField} from '@/components/shared/FormField'
import {AttributeValueInput} from '@/components/attributes/AttributeValueInput'
import type {AttributeDefinition} from '@/types/attribute-definition'

interface AttributeFieldsProps {
    definitions: AttributeDefinition[]
    values: Record<string, string>
    onChange: (definitionId: string, value: string) => void
    templateAttributeNames: string[]
    errors?: Record<string, string | null>
    testIdMap?: Record<string, string>
}

/**
 * Renders one {@link AttributeValueInput} per definition wrapped in a
 * {@link FormField}. Used by every create form that drives an
 * attribute-typed entity (party, identity, resource, function group, target
 * system). The field is marked required when the definition itself is required
 * OR when the attribute drives the type's display-name template.
 */
export function AttributeFields({
    definitions, values, onChange, templateAttributeNames, errors, testIdMap,
}: AttributeFieldsProps) {
    return (
        <>
            {definitions.map((def) => {
                const htmlFor = testIdMap?.[def.name]
                    || (def.name === 'derivedDisplayName'
                        ? 'input-resource-display-name'
                        : def.name === 'nativeKey'
                        ? 'input-resource-native-key'
                        : def.name === 'dataClassification'
                        ? 'select-classification'
                        : `attr-${def.name}`)

                return (
                    <FormField
                        key={def.id}
                        label={def.displayName || def.name}
                        htmlFor={htmlFor}
                        required={def.required || templateAttributeNames.includes(def.name)}
                        error={errors?.[def.id] || errors?.[def.name] || undefined}
                    >
                        <AttributeValueInput
                            definition={def}
                            value={values[def.id] ?? ''}
                            onChange={(v) => onChange(def.id, v)}
                            showLabel={false}
                            showHint
                            testId={htmlFor}
                        />
                    </FormField>
                )
            })}
        </>
    )
}
